import axios, { AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'
import { message } from 'ant-design-vue'
import type { ApiResponse } from '@/types/api'
import { AuthExpiredError, BusinessError, HttpError } from '@/utils/errors'

let isHandlingExpiry = false
let expiryHandler: (() => Promise<void> | void) | undefined

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: 15_000,
})

export function setAuthExpiryHandler(handler: () => Promise<void> | void): void {
  expiryHandler = handler
}

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const raw = localStorage.getItem('zsj-admin-session')
  if (!raw) return config
  try {
    const token = (JSON.parse(raw) as { token?: unknown }).token
    if (typeof token === 'string' && token) config.headers.set('token', token)
  } catch {
    localStorage.removeItem('zsj-admin-session')
  }
  return config
})

async function expireSession(error: AuthExpiredError): Promise<never> {
  if (!isHandlingExpiry) {
    isHandlingExpiry = true
    try {
      localStorage.removeItem('zsj-admin-session')
      message.error('登录状态已失效，请重新登录')
      await expiryHandler?.()
    } finally {
      window.setTimeout(() => {
        isHandlingExpiry = false
      }, 0)
    }
  }
  throw error
}

apiClient.interceptors.response.use(
  async (response: AxiosResponse<ApiResponse<unknown>>) => {
    let body = response.data
    if (response.config.responseType === 'blob' || response.config.responseType === 'arraybuffer') {
      const contentType = String(response.headers['content-type'] ?? '')
      if (!contentType.includes('json')) return response
      const binary = response.data as unknown as Blob | ArrayBuffer
      const text = binary instanceof Blob ? await binary.text() : new TextDecoder().decode(binary)
      try {
        body = JSON.parse(text) as ApiResponse<unknown>
      } catch {
        throw new BusinessError(-1, '导出失败：服务器返回了无效的数据')
      }
      if (body?.code === 200) throw new BusinessError(-1, '导出失败：服务器未返回文件')
    }
    const code = Number(body?.code)
    if (code === 1201 || code === 10001)
      return expireSession(new AuthExpiredError(code, body.msg || '登录已失效'))
    if (body?.code !== 200) throw new BusinessError(body?.code ?? -1, body?.msg || '业务请求失败')
    return response
  },
  async (error: AxiosError) => {
    if (error.code === 'ERR_CANCELED') throw error
    if (error.response?.status === 401)
      return expireSession(new AuthExpiredError(401, '登录已失效', { cause: error }))
    throw new HttpError(error.response?.status ?? 0, error.message || '网络请求失败', {
      cause: error,
    })
  },
)

export async function requestData<T>(promise: Promise<AxiosResponse<ApiResponse<T>>>): Promise<T> {
  const response = await promise
  return response.data.data
}
